import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { MAX_NUMBER_VALUE } from 'constants/number';
import { rentCalculatedPurposeOptions } from 'constants/options';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { checkIsLoanPurposeTypeBpl, checkIsPurchaseFlow } from 'utils/loanApplication';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import {
  customDividerStyles,
  customIconStyles,
  customIconWrapperStyles,
  customTitleStyles,
  customTitleWrapperStyles,
} from '../MainContent.styles';

export const proposedPropertyExpensesFields = {
  principalAndInterest: {
    fieldId: 'principalAndInterest',
    type: FieldType.INPUT,
    nameInModel: 'proposedPropertyExpenses.principalAndInterest',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Principal & Interest',
      label: 'Principal & Interest',
      required: true,
      disabled: true,
      variant: 'outlined',
    },
  },
  secondaryFinancing: {
    fieldId: 'secondaryFinancing',
    type: FieldType.INPUT,
    nameInModel: 'proposedPropertyExpenses.secondaryFinancing',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Other Financing',
      label: 'Other Financing',
      disabled: true,
      variant: 'outlined',
    },
  },
  pmi: {
    fieldId: 'pmi',
    type: FieldType.INPUT,
    nameInModel: 'proposedPropertyExpenses.pmi',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Mortgage Insurance',
      label: 'Mortgage Insurance',
      disabled: true,
      required: true,
      minValue: 0,
      variant: 'outlined',
    },
  },
  pmiManualUpdate: {
    fieldId: 'pmiManualUpdate',
    type: FieldType.SWITCH,
    nameInModel: 'proposedPropertyExpenses.pmiManualUpdate',
    inputProps: {
      label: 'MI Override:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_EXPENSES_FORM_MI_OVERRIDE_CHECKBOX,
    },
  },
  monthlyHomeownersInsurance: {
    fieldId: 'monthlyHomeownersInsurance',
    type: FieldType.INPUT,
    nameInModel: 'proposedPropertyExpenses.monthlyHomeownersInsurance',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Homeowners Insurance',
      label: 'Homeowners Insurance',
      required: true,
      minValue: 0,
      variant: 'outlined',
    },
  },
  homeownersInsurancePolicyDueDate: {
    fieldId: 'homeownersInsurancePolicyDueDate',
    type: FieldType.DATE,
    nameInModel: 'proposedPropertyExpenses.homeownersInsurancePolicyDueDate',
    inputProps: {
      format: COMMON_DATE_FORMAT,
      label: 'Policy Due Date',
      required: false,
      clearable: true,
      variant: 'outlined',
      dataTestId:
        PropertyAndLoanTestIds.PROPERTY_EXPENSES_FORM_HOMEOWNERS_INSURANCE_POLICY_DUE_DATE_INPUT,
    },
  },
  associationFeesAmount: {
    fieldId: 'associationFeesAmount',
    type: FieldType.INPUT,
    nameInModel: 'proposedPropertyExpenses.associationFeesAmount',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'HOA Dues',
      label: 'HOA Dues',
      required: false,
      minValue: 0,
      variant: 'outlined',
    },
  },
  rentCalculatedBasedOn: {
    fieldId: 'rentCalculatedBasedOn',
    type: FieldType.RADIO,
    nameInModel: 'proposedPropertyExpenses.rentCalculatedBasedOn',
    inputProps: {
      label: 'Rent calculated based on',
      disabled: false,
      required: false,
      options: rentCalculatedPurposeOptions,
      align: ItemGroupAlignment.VERTICAL,
      labelPlacement: ItemLabelPlacement.END,
      columns: 4,
    },
  },
  monthlyRentalIncome: {
    fieldId: 'monthlyRentalIncome',
    type: FieldType.INPUT,
    nameInModel: `proposedPropertyExpenses.monthlyRentalIncome`,
    inputProps: {
      label: 'Gross Rent',
      placeholder: 'Gross Rent',
      required: false,
      minValue: 0,
      type: FieldInputType.CURRENCY,
    },
  },
  mortgagesPayment: {
    fieldId: 'mortgagesPayment',
    type: FieldType.INPUT,
    nameInModel: `proposedPropertyExpenses.mortgagesPayment`,
    noValidate: true,
    noObserve: true,
    inputProps: {
      label: 'Mortgages Payment(s)',
      disabled: true,
      required: false,
      type: FieldInputType.CURRENCY,
      maxValue: 2 * MAX_NUMBER_VALUE,
    },
  },
  expenses: {
    fieldId: 'expenses',
    type: FieldType.INPUT,
    nameInModel: `proposedPropertyExpenses.expenses`,
    noValidate: true,
    inputProps: {
      label: 'Expenses',
      disabled: true,
      required: false,
      type: FieldInputType.CURRENCY,
    },
  },
  netMonthlyRentalIncome: {
    fieldId: 'netMonthlyRentalIncome',
    type: FieldType.INPUT,
    nameInModel: `proposedPropertyExpenses.netMonthlyRentalIncome`,
    noValidate: true,
    inputProps: {
      label: 'Net Rent',
      disabled: true,
      required: false,
      allowNegative: true,
      type: FieldInputType.CURRENCY,
    },
  },
};

export const existingPropertyExpensesFields = {
  principalAndInterest: {
    fieldId: 'principalAndInterest',
    type: FieldType.INPUT,
    nameInModel: 'existingPropertyExpenses.principalAndInterest',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Principal & Interest',
      label: 'Principal & Interest',
      required: true,
      minValue: 0,
      variant: 'outlined',
    },
  },
  secondaryFinancing: {
    fieldId: 'secondaryFinancing',
    type: FieldType.INPUT,
    nameInModel: 'existingPropertyExpenses.secondaryFinancing',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Secondary Financing',
      label: 'Secondary Financing',
      required: false,
      variant: 'outlined',
    },
  },
  mortgageInsurance: {
    fieldId: 'mortgageInsurance',
    type: FieldType.INPUT,
    nameInModel: 'existingPropertyExpenses.mortgageInsurance',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Mortgage Insurance',
      label: 'Mortgage Insurance',
      required: false,
      minValue: 0,
      variant: 'outlined',
    },
  },
  propertyTaxes: {
    fieldId: 'propertyTaxes',
    type: FieldType.INPUT,
    nameInModel: 'existingPropertyExpenses.propertyTaxes',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Property Taxes',
      label: 'Property Taxes',
      required: true,
      minValue: 0,
      variant: 'outlined',
    },
  },
  homeownersInsurance: {
    fieldId: 'homeownersInsurance',
    type: FieldType.INPUT,
    nameInModel: 'existingPropertyExpenses.homeownersInsurance',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Homeowners Insurance',
      label: 'Homeowners Insurance',
      required: true,
      minValue: 0,
      variant: 'outlined',
    },
  },
  supplementalInsurance: {
    fieldId: 'supplementalInsurance',
    type: FieldType.INPUT,
    nameInModel: 'existingPropertyExpenses.supplementalInsurance',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Supplemental Insurance',
      label: 'Supplemental Insurance',
      required: false,
      minValue: 0,
      variant: 'outlined',
    },
  },
  homeownersAssociation: {
    fieldId: 'homeownersAssociation',
    type: FieldType.INPUT,
    nameInModel: 'existingPropertyExpenses.homeownersAssociation',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'HOA Dues',
      label: 'HOA Dues',
      required: false,
      minValue: 0,
      variant: 'outlined',
    },
  },
  existingPropertyExpensesOtherExpenses: {
    fieldId: 'otherExpenses',
    type: FieldType.INPUT,
    nameInModel: 'existingPropertyExpenses.otherExpenses',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Other Expenses',
      label: 'Other Expenses',
      required: false,
      minValue: 0,
      variant: 'outlined',
    },
  },
  piti: {
    fieldId: 'piti',
    type: FieldType.INPUT,
    nameInModel: 'existingPropertyExpenses.piti',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'PITI',
      label: 'PITI',
      required: false,
      minValue: 0,
      variant: 'outlined',
    },
  },
  escrow: {
    fieldId: 'escrow',
    type: FieldType.SWITCH,
    nameInModel: 'existingPropertyExpenses.escrow',
    inputProps: {
      label: 'Escrow:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_EXPENSES_FORM_ESCROW_CHECKBOX,
    },
  },
};

export const propertyExpensesFieldsKeys = [
  ...Object.values(proposedPropertyExpensesFields).map(({ nameInModel }) => nameInModel),
  ...Object.values(existingPropertyExpensesFields).map(({ nameInModel }) => nameInModel),
];

// Hidden but still configured: the field keeps its value in the form model (and so in the save payload)
// while collapsing out of the grid.
const getHiddenField = field => ({
  ...field,
  inputProps: {
    ...field.inputProps,
    isHidden: true,
    wrapperStyles: { display: 'none' },
  },
});

// LEN-156: a chattel loan enters only homeowners insurance (+ policy due date) here — property taxes go
// through the escrow-line editor below the form. Every other proposed field stays configured but hidden
// so the subject-property save sends it back unchanged.
const chattelVisibleProposedFieldIds = [
  proposedPropertyExpensesFields.monthlyHomeownersInsurance.fieldId,
  proposedPropertyExpensesFields.homeownersInsurancePolicyDueDate.fieldId,
];

const getChattelProposedFields = fields =>
  fields.map(field =>
    chattelVisibleProposedFieldIds.includes(field.fieldId) ? field : getHiddenField(field),
  );

export const getProposedPropertyExpensesConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    isPmiManualUpdate,
    isAmountOfRentCollectedActive,
    loanPurposeType,
    loanPurpose,
    isQualifyingPrincipalAndInterestShown,
    proposedPrincipalAndInterestTitle,
    isChattel = false,
  }) => {
    const isBplLoanPurposeType = checkIsLoanPurposeTypeBpl(loanPurposeType);
    const isPurchaseFlow = checkIsPurchaseFlow(loanPurpose);

    // Subordinate HELOC (qualifying P&I differs from the note-rate P&I): the field displays the
    // qualifying value with an "i" tooltip breaking out both. The note-rate principalAndInterest field
    // stays in the config (hidden, collapsed out of the grid) so the form model keeps it — the save
    // guard and auto-lock wiring both key on proposedPropertyExpenses.principalAndInterest.
    const proposedPrincipalAndInterestFields = isQualifyingPrincipalAndInterestShown
      ? [
          {
            fieldId: 'qualifyingPrincipalAndInterest',
            type: FieldType.INPUT,
            nameInModel: 'proposedPropertyExpenses.qualifyingPrincipalAndInterest',
            inputProps: {
              type: FieldInputType.CURRENCY,
              decimalScale: 2,
              disabled: true,
              required: true,
              variant: 'outlined',
              additionalTitle: proposedPrincipalAndInterestTitle,
            },
          },
          getHiddenField(proposedPropertyExpensesFields.principalAndInterest),
        ]
      : [proposedPropertyExpensesFields.principalAndInterest];

    const proposedFields = [
      ...proposedPrincipalAndInterestFields,
      proposedPropertyExpensesFields.secondaryFinancing,
      {
        ...proposedPropertyExpensesFields.pmi,
        inputProps: {
          ...proposedPropertyExpensesFields.pmi.inputProps,
          disabled: !isPmiManualUpdate,
          minValue: isPmiManualUpdate ? 1 : 0,
        },
      },
      proposedPropertyExpensesFields.pmiManualUpdate,
      proposedPropertyExpensesFields.monthlyHomeownersInsurance,
      proposedPropertyExpensesFields.homeownersInsurancePolicyDueDate,
      proposedPropertyExpensesFields.associationFeesAmount,
    ];

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: isChattel ? getChattelProposedFields(proposedFields) : proposedFields,
          key: `proposed-property-expenses`,
          title: {
            label: 'Proposed Property Expenses',
            icon: InfoIcon,
            titleStyles: customTitleStyles,
            wrapperStyles: customTitleWrapperStyles,
            iconWrapperStyles: customIconWrapperStyles,
            iconStyles: customIconStyles,
            dividerStyles: customDividerStyles,
          },
        },
        ...(isAmountOfRentCollectedActive && !isChattel
          ? [
              {
                type: FieldType.GROUP,
                fields: [
                  ...(!isBplLoanPurposeType
                    ? [
                        {
                          ...proposedPropertyExpensesFields.rentCalculatedBasedOn,
                          inputProps: {
                            ...proposedPropertyExpensesFields.rentCalculatedBasedOn.inputProps,
                            disabled: isPurchaseFlow,
                          },
                        },
                      ]
                    : []),
                  proposedPropertyExpensesFields.monthlyRentalIncome,
                  proposedPropertyExpensesFields.mortgagesPayment,
                  proposedPropertyExpensesFields.expenses,
                  proposedPropertyExpensesFields.netMonthlyRentalIncome,
                ],
              },
            ]
          : []),
      ],
    };
  },
);

export const getExistingPropertyExpensesConfig = compose(fillConfigFieldsWithKeys, () => {
  return {
    groups: [
      {
        type: FieldType.GROUP,
        fields: [
          existingPropertyExpensesFields.principalAndInterest,
          existingPropertyExpensesFields.secondaryFinancing,
          existingPropertyExpensesFields.mortgageInsurance,
          existingPropertyExpensesFields.propertyTaxes,
          existingPropertyExpensesFields.homeownersInsurance,
          existingPropertyExpensesFields.supplementalInsurance,
          existingPropertyExpensesFields.homeownersAssociation,
          existingPropertyExpensesFields.existingPropertyExpensesOtherExpenses,
          existingPropertyExpensesFields.piti,
          existingPropertyExpensesFields.escrow,
        ],
        key: `existing-property-expenses`,
        title: {
          label: 'Existing Property Expenses',
          icon: InfoIcon,
          titleStyles: customTitleStyles,
          wrapperStyles: customTitleWrapperStyles,
          iconWrapperStyles: customIconWrapperStyles,
          iconStyles: customIconStyles,
          dividerStyles: customDividerStyles,
        },
      },
    ],
  };
});
